import './style.scss'
import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import ApiContext from 'src/provider/API/call-service';
import { AppSpan, AppText, CustomContainer, FlexRow, GridContainer, Input } from 'src/style';
import { DropdownOption } from 'src/model';
import { FiSearch } from 'react-icons/fi';

export const SearchableDropdown: React.FC<any> = ({
    leftMargin = 20,
    rightMargin = 20,
    topMargin = 5,
    bottomMargin = 20,
    width = 19.1,
    onSelect
}) => {
    const { API } = useContext(ApiContext)
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState<Array<DropdownOption>>([])
    const [searchText, setSearchText] = useState('')
    const [popupStyle, setPopupStyle] = useState<React.CSSProperties |null>({});
    const [isLoading, setIsLoading] = useState(false)
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const triggerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);

    const calculatePopupPosition = useCallback(() => {
        if (!triggerRef.current || !popupRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const popupRect = popupRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Initial positioning
        let topPosition = triggerRect.bottom + topMargin;
        let leftPosition = triggerRect.left + leftMargin;

        // Adjust left position to keep popup fully visible
        if (leftPosition + popupRect.width > viewportWidth - rightMargin) {
            leftPosition = viewportWidth - popupRect.width - rightMargin;
        }

        // Adjust top position to keep popup fully visible
        if (topPosition + popupRect.height > viewportHeight - bottomMargin) {
            topPosition = triggerRect.top - popupRect.height - bottomMargin;
        }

        setPopupStyle({
            position: 'fixed',
            top: `${topPosition}px`,
            left: `${leftPosition}px`,
            width: `${width}rem`,
            zIndex: 1000,
            opacity: 0,
            visibility: 'hidden',
        });

    }, [leftMargin, rightMargin, topMargin, bottomMargin, width]);

    const handleToggle = () => {
        setIsOpen((prev) => !prev);
        handleTextSearch(searchText)
    };

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(event.target as Node) &&
                !triggerRef.current?.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setPopupStyle(null)
            }
        },
        [popupRef, triggerRef]
    );

    useEffect(() => {
        if (isOpen) {
            calculatePopupPosition();
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('resize', calculatePopupPosition);
            window.addEventListener('scroll', calculatePopupPosition);

            setTimeout(() => {
                setPopupStyle((prevStyle) => ({
                  ...prevStyle,
                  opacity: 1,
                  visibility: 'visible',
                }));
              }, 0);
        }
        else {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', calculatePopupPosition);
            window.removeEventListener('scroll', calculatePopupPosition);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', calculatePopupPosition);
            window.removeEventListener('scroll', calculatePopupPosition);
        };
    }, [isOpen, calculatePopupPosition, handleClickOutside]);

    useEffect(() => {
        const timeout = setTimeout(() => {
          setDebouncedQuery(searchText);
        }, 300); // 300ms delay
    
        return () => clearTimeout(timeout);
      }, [searchText]);

    useEffect(() => {
        if (!debouncedQuery) return;

        handleTextSearch(searchText)
    }, [debouncedQuery]);

    async function handleTextSearch (text: string) {
        //setIsOpen(true)

        setIsLoading(true)
        const response = await API.getSearchedData(text)
        setIsLoading(false)
        if (response) setOptions(response)
    }

    const handleSelect = (value: string) => {
        if (onSelect) onSelect(value);
        setIsOpen(false);
    };

    return (
        <div style={{ position: 'relative' }}>
            <div
                ref={triggerRef}
                style={{ display: 'inline-block', cursor: 'pointer' }}
            >
                <CustomContainer
                    radius='0.8' borderColor='#344054'
                    leftPadding='1.5' rightPadding='1.5'
                    bgColor='#344054' width='51.9'
                    shadow='0px 1px 2px rgba(16, 24, 40, 0.05)'
                    className='header-search-box'
                >
                    <FlexRow>
                        <FiSearch fontSize='1.5rem' color='#ffffff' />
                        <Input 
                            height='4' leftPadding='1' color='#ffffff'
                            placeholder='Search apps & services' 
                            onChange={(e) => setSearchText(e.target.value)} 
                            onFocus={handleToggle}
                        />
                    </FlexRow>
                </CustomContainer>
            </div>
            { isOpen && (
                <div
                    ref={popupRef}
                    style={popupStyle!}
                    className="dropdown-content-container show searchable-dropdown-container"
                >
                    {  (isLoading || options?.length === 0) ?
                            <GridContainer topMargin='1.5'>
                                {
                                    isLoading ? 
                                        <AppSpan textSize='3.5' color='#0D968F' className='fa fa-spinner fa-spin' />
                                        : <AppText>No match found</AppText>
                                }
                            </GridContainer>
                            :
                            options?.map((option: DropdownOption, index: number) => (
                                <CustomContainer
                                    padding="1"
                                    minHeight="3.6"
                                    className="dropdown-item-wrapper"
                                    key={index}
                                    onClick={() => handleSelect(option.value)}
                                >
                                    <AppSpan fontWeight="500" cursor="pointer">
                                        {option.label || option.value}
                                    </AppSpan>
                                </CustomContainer>
                            ))
                    }
                </div>
            )}
        </div>
    );
};